import { useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import BulkPayoutDialog from '../../accountant/bulkPayoutDialog';
import { handlePriceWithDate, handlePriceWithRM } from "../../../utils/priceUtils";
import { convertDateTimeToDateString } from "../../../utils/datetimeUtils";
import { Box, Chip, Typography } from "@mui/material";
import { DownloadIcon } from "@phosphor-icons/react";
import { download, generateCsv, mkConfig } from "export-to-csv";
import CommissionProgressBar from "./commissionProgressBar";
import useTableState from "../../../hooks/useTableState";
import { sendClaimEmailNow } from "../../../api/clapsleyApi";
import { facetFilterOptions } from '../facetFilter';

// Commission CLAIM readiness (backend ClaimState) — a separate axis from the sale's project
// status. Ready = the customer hit a clean 60%/100% milestone; Disputed = off-milestone.
const CLAIM_STATE_META = {
    ReadyFull:    { label: 'Ready 100%', sx: { backgroundColor: '#DCFCE7', color: '#166534', fontWeight: 600 } },
    ReadyPartial: { label: 'Ready 60%',  sx: { backgroundColor: '#DCFCE7', color: '#166534', fontWeight: 600 } },
    Disputed:     { label: 'Disputed',   sx: { backgroundColor: '#FEE2E2', color: '#991B1B', fontWeight: 600 } },
    NotReady:     { label: 'Not ready',  sx: { backgroundColor: '#F1F5F9', color: '#64748B', fontWeight: 600 } },
};

// All available column definitions
const ALL_COLUMNS = (createStatusCell) => ({
    salesId: { id: 'salesId', header: 'Sales ID', accessorFn: row => row.salesDetailsForCommissionProgress?.salesId, size: 80 },
    depositDate: {
        id: 'depositDate', header: 'Deposit Date',
        accessorFn: row => row.salesDetailsForCommissionProgress?.depositOn ? new Date(row.salesDetailsForCommissionProgress.depositOn).getTime() : null,
        Cell: ({ row }) => convertDateTimeToDateString(row.original.salesDetailsForCommissionProgress?.depositOn),
        size: 110,
    },
    customer: { id: 'customer', header: 'Customer', accessorFn: row => row.salesDetailsForCommissionProgress?.customerName ?? '', size: 140 },
    productCategory: { id: 'productCategory', header: 'Category', accessorFn: row => row.salesDetailsForCommissionProgress?.productCategory ?? '', size: 100 },
    productEnergy: { id: 'productEnergy', header: 'Energy', accessorFn: row => `${row.salesDetailsForCommissionProgress?.energy ?? ""} kWp`.trim(), size: 80 },
    oriPrice: {
        id: 'oriPrice', header: 'Ori Price', size: 100,
        accessorFn: row => row.salesDetailsForCommissionProgress?.originalPrice,
        Cell: ({ row }) => { const v = row.original.salesDetailsForCommissionProgress?.originalPrice; return v != null ? handlePriceWithRM(v) : ""; },
    },
    asternDiscount: {
        id: 'asternDiscount', header: 'Astern Disc.', size: 100,
        accessorFn: row => row.salesDetailsForCommissionProgress?.discountByAstern,
        Cell: ({ row }) => { const v = row.original.salesDetailsForCommissionProgress?.discountByAstern; return v != null ? handlePriceWithRM(v) : ""; },
    },
    agentDiscount: {
        id: 'agentDiscount', header: 'Agent Disc.', size: 100,
        accessorFn: row => row.salesDetailsForCommissionProgress?.discountByAgent,
        Cell: ({ row }) => { const v = row.original.salesDetailsForCommissionProgress?.discountByAgent; return v != null ? handlePriceWithRM(v) : ""; },
    },
    // Upline / Team discount (spec §1) — funded by the seller's upline(s) (comes off DOC)
    // and the leader(s) (comes off TOC). Same column pattern as Astern/Agent above.
    uplineDiscount: {
        id: 'uplineDiscount', header: 'Upline Disc.', size: 100,
        accessorFn: row => row.salesDetailsForCommissionProgress?.discountByUpline,
        Cell: ({ row }) => { const v = row.original.salesDetailsForCommissionProgress?.discountByUpline; return v != null ? handlePriceWithRM(v) : ""; },
    },
    teamDiscount: {
        id: 'teamDiscount', header: 'Team Disc.', size: 100,
        accessorFn: row => row.salesDetailsForCommissionProgress?.discountByTeam,
        Cell: ({ row }) => { const v = row.original.salesDetailsForCommissionProgress?.discountByTeam; return v != null ? handlePriceWithRM(v) : ""; },
    },
    finalPrice: {
        id: 'finalPrice', header: 'Final Price', size: 100,
        accessorFn: row => row.salesDetailsForCommissionProgress?.finalPrice,
        Cell: ({ row }) => { const v = row.original.salesDetailsForCommissionProgress?.finalPrice; return v != null ? handlePriceWithRM(v) : ""; },
    },
    agent: { id: 'agent', header: 'Agent', accessorFn: row => row.salesDetailsForCommissionProgress?.agentName ?? '', size: 120 },
    coAgent: { id: 'coAgent', header: 'Co-Agent (Offline)', accessorFn: row => row.salesDetailsForCommissionProgress?.coAgentName ?? '', size: 120 },
    // ── Referral Fee list only ───────────────────────────────────────────────
    referrer: { id: 'referrer', header: 'Referrer', accessorFn: row => row.salesDetailsForCommissionProgress?.referrerName ?? '', size: 130 },
    referralPaidBy: {
        id: 'referralPaidBy', header: 'Borne By', size: 90,
        accessorFn: row => row.salesDetailsForCommissionProgress?.referralPaidBy ?? '',
        // Astern is the default and the common case, so it is the quiet one. Agent-borne is
        // the exception and the one worth spotting in a long list — it means an agent's own
        // commission paid for this.
        Cell: ({ row }) => {
            const v = row.original.salesDetailsForCommissionProgress?.referralPaidBy;
            if (!v) return '';
            const agent = v === 'Agent';
            return (
                <Chip size="small" label={agent ? 'Agent' : 'Astern'}
                    sx={{
                        height: 20, fontSize: '0.66rem', fontWeight: 700,
                        bgcolor: agent ? '#FEF3C7' : '#EFF6FF',
                        color: agent ? '#92400E' : '#0E8FD1',
                    }} />
            );
        },
    },
    referralRate: {
        id: 'referralRate', header: 'Rate', size: 70,
        accessorFn: row => row.salesDetailsForCommissionProgress?.referralRatePct,
        Cell: ({ row }) => {
            const v = row.original.salesDetailsForCommissionProgress?.referralRatePct;
            return v != null ? `${Number(v)}%` : '';
        },
    },
    referralPayout: {
        id: 'referralPayout', header: 'Ref Fee Payout', size: 140,
        // Has the REFERRER been paid? Deliberately not the agent's commission progress and
        // not the sale's status — both of those answer a different question and neither
        // moves when a referral fee is settled.
        //
        // Agent-borne fees ride the referrer's normal commission slip, so their real
        // PaymentStatus is the answer. Astern-borne fees have no payment record at all
        // yet — the payout step is not built — so they read "Not paid", which is true
        // rather than reassuring.
        accessorFn: row => {
            const d = row.salesDetailsForCommissionProgress;
            const cp = row.commissionProgressDto;
            const paid = (Number(cp?.firstPayment) || 0) + (Number(cp?.secondPayment) || 0);
            if (paid > 0) return 'Paid';
            if (d?.referralPaidBy === 'Agent') return cp?.paymentStatus || 'Not paid';
            return 'Not paid';
        },
        Cell: ({ row }) => {
            const d = row.original.salesDetailsForCommissionProgress;
            const cp = row.original.commissionProgressDto;
            const due = Number(cp?.commPay) || 0;
            const paid = (Number(cp?.firstPayment) || 0) + (Number(cp?.secondPayment) || 0);
            const isPaid = paid > 0 && paid >= due;
            const part = paid > 0 && paid < due;
            const label = isPaid ? 'Paid' : part ? 'Part paid' : 'Not paid';
            const bg = isPaid ? '#F0FDF4' : part ? '#FEF3C7' : '#F1F5F9';
            const fg = isPaid ? '#166534' : part ? '#92400E' : '#475569';
            return (
                <Box>
                    <Chip size="small" label={label}
                        sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700, bgcolor: bg, color: fg }} />
                    {paid > 0 && (
                        <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8', mt: 0.25 }}>
                            {handlePriceWithRM(paid)} of {handlePriceWithRM(due)}
                        </Typography>
                    )}
                    {paid === 0 && d?.referralPaidBy === 'Astern' && (
                        <Typography sx={{ fontSize: '0.64rem', color: '#94A3B8', mt: 0.25 }}>
                            paid by Astern
                        </Typography>
                    )}
                </Box>
            );
        },
    },
    // Where the fee is actually paid to. On this list it is not a nicety: an Astern-borne
    // fee is settled by bank transfer out of this page, and a referrer with no bank details
    // on file is a payment that cannot be made — better seen in the list than discovered on
    // payment day. (user 2026-10-14: "can also show bank details?")
    referrerBank: {
        id: 'referrerBank', header: 'Bank Details', size: 190,
        // Sorts/filters on the bank name so a column filter of "Maybank" works; the cell
        // shows the account number and beneficiary underneath.
        accessorFn: row => row.salesDetailsForCommissionProgress?.referrerBankName ?? '',
        Cell: ({ row }) => {
            const d = row.original.salesDetailsForCommissionProgress;
            const bank = d?.referrerBankName;
            const acc = d?.referrerBankAccountNumber;
            // Beneficiary is only worth the line when it differs from the referrer's own
            // name — repeating the name under the name tells nobody anything.
            const ben = d?.referrerBankBeneficiaryName;
            const benDiffers = ben && ben.trim().toLowerCase() !== (d?.referrerName || '').trim().toLowerCase();
            if (!bank && !acc) {
                return (
                    <Chip size="small" label="No bank details"
                        sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700, bgcolor: '#FEF2F2', color: '#B91C1C' }} />
                );
            }
            return (
                <Box>
                    <Typography sx={{ fontSize: '0.75rem', color: '#374151', fontWeight: 600 }}>{bank || '—'}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>{acc || 'no account no.'}</Typography>
                    {benDiffers && (
                        <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8' }}>a/c name: {ben}</Typography>
                    )}
                </Box>
            );
        },
    },
    referrerIsCustomer: {
        id: 'referrerIsCustomer', header: 'Was Customer?', size: 150,
        // Sorts and filters on the plain word, so "Customer" can be typed into the filter.
        accessorFn: row => (row.salesDetailsForCommissionProgress?.referrerIsCustomer ? 'Customer' : 'No'),
        // When they ARE a customer, whose customer matters: a referral from somebody another
        // agent sold to is a different conversation from one you brought in yourself.
        Cell: ({ row }) => {
            const d = row.original.salesDetailsForCommissionProgress;
            if (!d?.referrerIsCustomer) {
                return <span style={{ color: '#94A3B8' }}>Not a customer</span>;
            }
            const who = [d.referrerCustomerAgentName, d.referrerCustomerCoAgentName]
                .filter(Boolean).join(' + ');
            // How many times they have bought from us, because one purchase and five are a
            // different conversation. The detail page lists them with who sold each.
            const n = (d.referrerCustomerSales || []).length;
            return (
                <Box>
                    <Chip size="small" label={n > 1 ? `Customer ×${n}` : 'Customer'}
                        sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700, bgcolor: '#F0FDF4', color: '#166534' }} />
                    {who && (
                        <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8', mt: 0.25 }}>
                            via {who}{n > 1 ? ' (latest)' : ''}
                        </Typography>
                    )}
                </Box>
            );
        },
    },
    commissionType: { id: 'commissionType', header: 'Comm Type', accessorFn: row => row.commissionProgressDto?.commPayType ?? '', size: 90 },
    paymentTo: { id: 'paymentTo', header: 'Payment To', accessorFn: row => row.commissionProgressDto?.paymentToName ?? '', size: 120 },
    commission: {
        id: 'commission', header: 'Commission', size: 100,
        accessorFn: row => row.commissionProgressDto?.commPay,
        // A NEGATIVE commission is legitimate: an Upline/Team discount larger than this
        // recipient's DOC/TOC override means the bearer pays (spec §12 decision ③). Never
        // clamped — shown in red so it reads as an amount owed back.
        Cell: ({ row }) => {
            const v = row.original.commissionProgressDto?.commPay;
            if (v == null) return "";
            return <span style={{ color: v < 0 ? '#DC2626' : 'inherit', fontWeight: v < 0 ? 700 : 'inherit' }}>{handlePriceWithRM(v)}</span>;
        },
    },
    firstPayment: {
        id: 'firstPayment', header: '1st Pay', size: 100,
        accessorFn: row => row.commissionProgressDto?.firstPayment,
        Cell: ({ row }) => { const v = row.original.commissionProgressDto?.firstPayment; return v != null ? handlePriceWithRM(v) : ""; },
    },
    firstPaymentDate: {
        id: 'firstPaymentDate', header: '1st Pay Date', size: 100,
        accessorFn: row => row.commissionProgressDto?.firstPaymentOn,
        Cell: ({ row }) => { const v = row.original.commissionProgressDto?.firstPaymentOn; return v ? convertDateTimeToDateString(v) : ""; },
    },
    secondPayment: {
        id: 'secondPayment', header: '2nd Pay', size: 100,
        accessorFn: row => row.commissionProgressDto?.secondPayment,
        Cell: ({ row }) => { const v = row.original.commissionProgressDto?.secondPayment; return v != null ? handlePriceWithRM(v) : ""; },
    },
    secondPaymentDate: {
        id: 'secondPaymentDate', header: '2nd Pay Date', size: 100,
        accessorFn: row => row.commissionProgressDto?.secondPaymentOn,
        Cell: ({ row }) => { const v = row.original.commissionProgressDto?.secondPaymentOn; return v ? convertDateTimeToDateString(v) : ""; },
    },
    progress: {
        id: 'progress', header: 'Comm Progress', size: 110,
        accessorFn: row => {
            const cp = row.commissionProgressDto;
            if (!cp || !cp.commPay) return 0;
            const allRepay = (cp.repayment || 0) + (cp.repayment2 || 0) + (cp.repayment3 || 0) + (cp.repayment4 || 0) + (cp.repayment5 || 0);
            const paid = (cp.firstPayment || 0) + (cp.secondPayment || 0) + allRepay;
            return paid / cp.commPay * 100;
        },
        Cell: ({ row }) => {
            const cp = row.original?.commissionProgressDto;
            const allRepay = (cp?.repayment || 0) + (cp?.repayment2 || 0) + (cp?.repayment3 || 0) + (cp?.repayment4 || 0) + (cp?.repayment5 || 0);
            const paid = (cp?.firstPayment || 0) + (cp?.secondPayment || 0) + allRepay;
            return <CommissionProgressBar paid={paid} total={cp?.commPay || 0} />;
        },
    },
    custProgress: {
        id: 'custProgress', header: 'Cust Progress', size: 110,
        accessorFn: row => {
            const sd = row.salesDetailsForCommissionProgress;
            // Denominator = the backend EffectiveTotal (customer-facing; SST-inclusive for
            // commercial). Single source of truth; falls back to final+add-on for old payloads.
            const total = Number(sd?.effectiveTotal) > 0 ? Number(sd.effectiveTotal)
                : (Number(sd?.quotationTotal) > 0 ? Number(sd.quotationTotal) : ((sd?.finalPrice || 0) + (sd?.addOnPrice || 0)));
            if (!total) return 0;
            return (sd?.paidTotal || 0) / total * 100;
        },
        Cell: ({ row }) => {
            const sd = row.original?.salesDetailsForCommissionProgress;
            const total = Number(sd?.effectiveTotal) > 0 ? Number(sd.effectiveTotal)
                : (Number(sd?.quotationTotal) > 0 ? Number(sd.quotationTotal) : ((Number(sd?.finalPrice) || 0) + (Number(sd?.addOnPrice) || 0)));
            return (
                <CommissionProgressBar
                    paid={Number(sd?.paidTotal) || 0}
                    total={total}
                />
            );
        },
    },
    claimState: {
        id: 'claimState', header: 'Claim', size: 100,
        filterVariant: 'select',
        filterSelectOptions: ['Ready 100%', 'Ready 60%', 'Disputed', 'Not ready'],
        accessorFn: row => CLAIM_STATE_META[row.salesDetailsForCommissionProgress?.claimState]?.label ?? 'Not ready',
        Cell: ({ row }) => {
            const st = row.original?.salesDetailsForCommissionProgress?.claimState || 'NotReady';
            const meta = CLAIM_STATE_META[st] || CLAIM_STATE_META.NotReady;
            return <Chip sx={meta.sx} label={meta.label} size="small" />;
        },
    },
    status: {
        id: 'status', header: 'Status', size: 100,
        filterVariant: 'select',
        filterSelectOptions: ['Paid', 'Pending', 'Overpaid'],
        accessorFn: row => {
            const cp = row.commissionProgressDto;
            if (!cp || !cp.commPay) return 'Pending';
            const allRepay = (cp.repayment || 0) + (cp.repayment2 || 0) + (cp.repayment3 || 0) + (cp.repayment4 || 0) + (cp.repayment5 || 0);
            const paid = (cp.firstPayment || 0) + (cp.secondPayment || 0) + allRepay;
            const pct = paid / cp.commPay * 100;
            if (pct > 100.01) return 'Overpaid';
            if (pct >= 99.99) return 'Paid';
            return 'Pending';
        },
        Cell: ({ row }) => {
            const cp = row.original?.commissionProgressDto;
            if (!cp || !cp.commPay) return <Chip color="default" label="Pending" size="small" />;
            const allRepay = (cp.repayment || 0) + (cp.repayment2 || 0) + (cp.repayment3 || 0) + (cp.repayment4 || 0) + (cp.repayment5 || 0);
            const paid = (cp.firstPayment || 0) + (cp.secondPayment || 0) + allRepay;
            const pct = paid / cp.commPay * 100;
            if (pct > 100.01) return <Chip color="error" label="Overpaid" size="small" />;
            if (pct >= 99.99) return <Chip color="success" label="Paid" size="small" />;
            return <Chip sx={{ backgroundColor: '#FEF3C7', color: '#92400E', fontWeight: 600 }} label="Pending" size="small" />;
        },
    },
    paymentStatus: {
        id: 'paymentStatus', header: 'Pay Status', size: 100,
        accessorFn: row => row.commissionProgressDto?.paymentStatus,
        Cell: createStatusCell(row => row?.commissionProgressDto?.paymentStatus),
    },
    repayment: {
        id: 'repayment', header: '3rd Pay', size: 100,
        accessorFn: row => row.commissionProgressDto?.repayment,
        Cell: ({ row }) => {
            const cp = row.original.commissionProgressDto;
            return (cp?.repayment != null) ? handlePriceWithDate(cp.repayment, cp.repaymentOn) : "";
        },
    },
    repaymentStatus: {
        id: 'repaymentStatus', header: '3rd Pay Status', size: 100,
        accessorFn: row => row.commissionProgressDto?.repaymentStatus,
        Cell: createStatusCell(row => row?.commissionProgressDto?.repaymentStatus),
    },
    updatedBy: { id: 'updatedBy', header: 'Updated By', accessorKey: 'updatedBy', size: 100 },
    updatedOn: {
        id: 'updatedOn', header: 'Date', size: 100,
        accessorFn: (row) => { try { return new Date(row.updatedOn); } catch { return null; } },
        Cell: ({ row }) => row.original ? convertDateTimeToDateString(row.original.updatedOn) : "",
    },
    actions: null, // placeholder — built dynamically with onRowClick
});

// Default column order (all columns)
const DEFAULT_VISIBLE = [
    'salesId', 'depositDate', 'customer', 'productCategory', 'productEnergy',
    'oriPrice', 'asternDiscount', 'agentDiscount', 'uplineDiscount', 'teamDiscount', 'finalPrice',
    'agent', 'coAgent', 'commissionType', 'paymentTo', 'commission',
    'firstPayment', 'firstPaymentDate', 'secondPayment', 'secondPaymentDate',
    'progress', 'custProgress', 'claimState', 'status', 'repayment', 'updatedBy', 'updatedOn',
];

export default function GeneralCommissionProgressList({
    fetchData,
    onRowClick,
    visibleColumns,
    detailRoute,
    // Where a row goes when the destination is NOT a CommissionProgress record. All RF is
    // the case: an Astern-borne referral fee has no commission row at all, so there is no
    // commProgressId to route on and the default gate below would make the row dead. Given
    // a function, it decides the link for every row and the commProgressId gate is skipped.
    // (user 2026-10-14: "can click in like sales comm can see similar page? but its for referral")
    rowLink,
    // All Sales Comm only: two extra exports — the Astern "Ready to Claim" list (customer paid
    // exactly 60% or 100%) and "Pending Comm Claim" (commission owed, not yet fully claimed).
    // (user 2026-07-22)
    showClaimExports = false,
    // Bulk payout (2026-08-20) - opt-in, so it only shows where payments are keyed.
    showBulkPayout = false,
    onBulkDone,
    isSuperAdmin = false,
}) {
    const navigate = useNavigate();
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkRows, setBulkRows] = useState([]);
    const ts = useTableState('', { defaultSorting: [{ id: 'depositDate', desc: true }] });
    const statusMap = {
        'In Progress': { label: 'In Progress', color: 'info' },
        'Complete': { label: 'Complete', color: 'success' },
        'Completed': { label: 'Completed', color: 'success' },
        'Settled': { label: 'Settled', color: 'success' },
        'Underpaid': { label: 'Underpaid', color: 'warning' },
        'Overpaid': { label: 'Overpaid', color: 'error' },
    };

    const createStatusCell = (accessor) => ({ row }) => {
        const status = accessor(row.original);
        if (!status) return <span style={{ color: '#94A3B8', fontSize: '0.75rem' }}>{'\u2014'}</span>;
        const { label, color } = statusMap[status] ?? { label: status, color: 'default' };
        return <Chip color={color} label={label} size="small" />;
    };

    const allCols = ALL_COLUMNS(createStatusCell);

    // Build actions column dynamically since it needs onRowClick
    const actionsCol = (onRowClick || rowLink) ? {
        id: 'actions', header: '', size: 60, enableSorting: false, enableColumnFilter: false,
        // An Astern-borne referral fee has NO CommissionProgress row — it is kept out of the
        // commission pot on purpose — so it carries commProgressId 0. Linking "Edit" on one
        // would navigate to /accounting/commission/0 and open an editor for a record that
        // does not exist. There is nothing to edit, so there is no link. (user 2026-10-14)
        //
        // With a rowLink there IS somewhere to go, so the cell reads "View" and follows it —
        // the same destination the row click uses, for people who look for a link.
        Cell: ({ row }) => {
            const custom = rowLink ? rowLink(row.original) : null;
            if (custom) {
                return (
                    <span
                        onClick={(e) => { e.stopPropagation(); navigate(custom); }}
                        style={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 500, fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif" }}
                    >
                        View
                    </span>
                );
            }
            return Number(row.original.commProgressId) > 0 ? (
                <span
                    onClick={(e) => { e.stopPropagation(); navigate(`/accounting/commission/${row.original.commProgressId}`); }}
                    style={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 500, fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif" }}
                >
                    Edit
                </span>
            ) : null;
        },
    } : null;

    const colKeys = visibleColumns || DEFAULT_VISIBLE;
    const columns = useMemo(() => {
        const cols = colKeys.map(key => key === 'actions' ? actionsCol : allCols[key]).filter(Boolean);
        return cols;
    }, [colKeys, actionsCol]);

    // Export helpers
    const csvConfig = mkConfig({ filename: 'Commission_Data_Export', fieldSeparator: ',', decimalSeparator: '.', useKeysAsHeaders: true });

    const EXPORT_FIELD_MAP = {
        salesId: (r) => ({ 'Sales ID': r.salesDetailsForCommissionProgress?.salesId ?? '' }),
        depositDate: (r) => ({ 'Deposit Date': convertDateTimeToDateString(r.salesDetailsForCommissionProgress?.depositOn) }),
        customer: (r) => ({ 'Customer': r.salesDetailsForCommissionProgress?.customerName ?? '' }),
        productCategory: (r) => ({ 'Category': r.salesDetailsForCommissionProgress?.productCategory ?? '' }),
        productEnergy: (r) => ({ 'Energy (kWp)': r.salesDetailsForCommissionProgress?.energy ?? '' }),
        oriPrice: (r) => ({ 'Ori Price': r.salesDetailsForCommissionProgress?.originalPrice ?? '' }),
        asternDiscount: (r) => ({ 'Astern Disc.': r.salesDetailsForCommissionProgress?.discountByAstern ?? '' }),
        agentDiscount: (r) => ({ 'Agent Disc.': r.salesDetailsForCommissionProgress?.discountByAgent ?? '' }),
        uplineDiscount: (r) => ({ 'Upline Disc.': r.salesDetailsForCommissionProgress?.discountByUpline ?? '' }),
        teamDiscount: (r) => ({ 'Team Disc.': r.salesDetailsForCommissionProgress?.discountByTeam ?? '' }),
        finalPrice: (r) => ({ 'Final Price': r.salesDetailsForCommissionProgress?.finalPrice ?? '' }),
        referrer: (r) => ({ 'Referrer': r.salesDetailsForCommissionProgress?.referrerName ?? '' }),
        referralPayout: (r) => {
            const cp = r.commissionProgressDto;
            const due = Number(cp?.commPay) || 0;
            const paid = (Number(cp?.firstPayment) || 0) + (Number(cp?.secondPayment) || 0);
            return {
                'Ref Fee Due': due || '',
                'Ref Fee Paid': paid || '',
                'Ref Fee Payout': paid > 0 && paid >= due ? 'Paid' : paid > 0 ? 'Part paid' : 'Not paid',
            };
        },
        referralPaidBy: (r) => ({ 'Borne By': r.salesDetailsForCommissionProgress?.referralPaidBy ?? '' }),
        referralRate: (r) => ({ 'Rate %': r.salesDetailsForCommissionProgress?.referralRatePct ?? '' }),
        // Three separate columns, not one pretty string: this export is what a bank-payment
        // sheet gets built from, and a merged cell has to be taken apart again by hand.
        referrerBank: (r) => ({
            'Bank': r.salesDetailsForCommissionProgress?.referrerBankName ?? '',
            'Bank Account No': r.salesDetailsForCommissionProgress?.referrerBankAccountNumber ?? '',
            'Beneficiary Name': r.salesDetailsForCommissionProgress?.referrerBankBeneficiaryName ?? '',
        }),
        referrerIsCustomer: (r) => ({
            'Was Customer': r.salesDetailsForCommissionProgress?.referrerIsCustomer ? 'Yes' : 'No',
            // Their own purchase history, flattened into the export so it can be read without
            // opening each referral. (user 2026-10-14)
            'Own Purchases': (r.salesDetailsForCommissionProgress?.referrerCustomerSales || []).length,
            'Own Purchases Sold By': (r.salesDetailsForCommissionProgress?.referrerCustomerSales || [])
                .map(s => `#${s.salesId} ${[s.agentName, s.coAgentName].filter(Boolean).join(' + ')}`.trim())
                .join('; '),
            'Customer Of': [r.salesDetailsForCommissionProgress?.referrerCustomerAgentName,
                            r.salesDetailsForCommissionProgress?.referrerCustomerCoAgentName]
                           .filter(Boolean).join(' + '),
        }),
        agent: (r) => ({ 'Agent': r.salesDetailsForCommissionProgress?.agentName ?? '' }),
        coAgent: (r) => ({ 'CoAgent': r.salesDetailsForCommissionProgress?.coAgentName ?? '' }),
        commissionType: (r) => ({ 'Comm Type': r.commissionProgressDto?.commPayType ?? '' }),
        paymentTo: (r) => ({ 'Payment To': r.commissionProgressDto?.paymentToName ?? '' }),
        commission: (r) => ({ 'Commission': r.commissionProgressDto?.commPay ?? '' }),
        firstPayment: (r) => ({ '1st Pay': r.commissionProgressDto?.firstPayment ?? '' }),
        firstPaymentDate: (r) => ({ '1st Pay Date': convertDateTimeToDateString(r.commissionProgressDto?.firstPaymentOn) }),
        secondPayment: (r) => ({ '2nd Pay': r.commissionProgressDto?.secondPayment ?? '' }),
        secondPaymentDate: (r) => ({ '2nd Pay Date': convertDateTimeToDateString(r.commissionProgressDto?.secondPaymentOn) }),
        progress: (r) => {
            const cp = r.commissionProgressDto;
            // Every slot counts - counting only the first two showed a fully settled row
            // as part-paid once a 3rd Pay existed. (user 2026-08-21)
            const paid = (cp?.firstPayment || 0) + (cp?.secondPayment || 0) + (cp?.repayment || 0)
                       + (cp?.repayment2 || 0) + (cp?.repayment3 || 0) + (cp?.repayment4 || 0) + (cp?.repayment5 || 0);
            const pct = cp?.commPay ? Math.round(paid / cp.commPay * 100) : 0;
            return { 'Comm Progress %': pct };
        },
        custProgress: (r) => {
            const sd = r.salesDetailsForCommissionProgress;
            const total = Number(sd?.effectiveTotal) > 0 ? Number(sd.effectiveTotal)
                : (Number(sd?.quotationTotal) > 0 ? Number(sd.quotationTotal) : ((sd?.finalPrice || 0) + (sd?.addOnPrice || 0)));
            const pct = (sd?.paidPct != null) ? Number(sd.paidPct)
                : (total ? Math.round((sd?.paidTotal || 0) / total * 100) : 0);
            return { 'Cust Progress %': pct };
        },
        claimState: (r) => ({ 'Claim': CLAIM_STATE_META[r.salesDetailsForCommissionProgress?.claimState]?.label ?? 'Not ready' }),
        status: (r) => {
            const cp = r.commissionProgressDto;
            if (!cp || !cp.commPay) return { 'Status': 'Pending' };
            const allRepay = (cp.repayment || 0) + (cp.repayment2 || 0) + (cp.repayment3 || 0) + (cp.repayment4 || 0) + (cp.repayment5 || 0);
            const paid = (cp.firstPayment || 0) + (cp.secondPayment || 0) + allRepay;
            const pct = paid / cp.commPay * 100;
            const label = pct > 100.01 ? 'Overpaid' : pct >= 99.99 ? 'Paid' : 'Pending';
            return { 'Status': label };
        },
        paymentStatus: (r) => ({ 'Pay Status': r.commissionProgressDto?.paymentStatus ?? '' }),
        repayment: (r) => ({ '3rd Pay': r.commissionProgressDto?.repayment ?? '' }),
        repaymentDate: (r) => ({ '3rd Pay Date': convertDateTimeToDateString(r.commissionProgressDto?.repaymentOn) }),
        repaymentStatus: (r) => ({ '3rd Pay Status': r.commissionProgressDto?.repaymentStatus ?? '' }),
        // Slots 4-7 were never exported at all, so a row settled across several payments
        // looked half-paid in the CSV. The API has returned them all along. (user 2026-08-21)
        repayment2: (r) => ({ '4th Pay': r.commissionProgressDto?.repayment2 ?? '' }),
        repayment2Date: (r) => ({ '4th Pay Date': convertDateTimeToDateString(r.commissionProgressDto?.repayment2On) }),
        repayment3: (r) => ({ '5th Pay': r.commissionProgressDto?.repayment3 ?? '' }),
        repayment3Date: (r) => ({ '5th Pay Date': convertDateTimeToDateString(r.commissionProgressDto?.repayment3On) }),
        repayment4: (r) => ({ '6th Pay': r.commissionProgressDto?.repayment4 ?? '' }),
        repayment4Date: (r) => ({ '6th Pay Date': convertDateTimeToDateString(r.commissionProgressDto?.repayment4On) }),
        repayment5: (r) => ({ '7th Pay': r.commissionProgressDto?.repayment5 ?? '' }),
        repayment5Date: (r) => ({ '7th Pay Date': convertDateTimeToDateString(r.commissionProgressDto?.repayment5On) }),
        totalPaid: (r) => {
            const cp = r.commissionProgressDto || {};
            const t = (cp.firstPayment || 0) + (cp.secondPayment || 0) + (cp.repayment || 0)
                    + (cp.repayment2 || 0) + (cp.repayment3 || 0) + (cp.repayment4 || 0) + (cp.repayment5 || 0);
            return { 'Total Paid': Math.round(t * 100) / 100 };
        },
        balance: (r) => {
            const cp = r.commissionProgressDto || {};
            const t = (cp.firstPayment || 0) + (cp.secondPayment || 0) + (cp.repayment || 0)
                    + (cp.repayment2 || 0) + (cp.repayment3 || 0) + (cp.repayment4 || 0) + (cp.repayment5 || 0);
            return { 'Balance': Math.round(((cp.commPay || 0) - t) * 100) / 100 };
        },
        updatedBy: (r) => ({ 'Updated By': r.updatedBy ?? '' }),
        updatedOn: (r) => ({ 'Date': convertDateTimeToDateString(r.updatedOn) }),
    };

    // Export writes EVERY mapped field, not just the visible columns. Following the on-screen
    // columns is what hid pay slots 3-7 from the CSV - the data was always there. Applies to
    // Export All and Export Selected alike, so both carry the full record. (user 2026-08-21)
    const exportKeys = Object.keys(EXPORT_FIELD_MAP);

    const mapCommForExport = (data) => data.map(r => {
        const row = {};
        exportKeys.forEach(k => Object.assign(row, EXPORT_FIELD_MAP[k](r)));
        return row;
    });

    const handleExportAll = () => {
        const csv = generateCsv(csvConfig)(mapCommForExport(fetchData || []));
        download(csvConfig)(csv);
    };


    // ── Astern claim exports (All Sales Comm) ──────────────────────────────────
    // Per-row claim metrics: customer paid %, commission claimed (paid out) + its %.
    const claimMetrics = (r) => {
        const sd = r.salesDetailsForCommissionProgress || {};
        const cp = r.commissionProgressDto || {};
        // Denominator + claim state come from the backend (customer-facing QuotationTotal,
        // one source of truth). Legacy fallbacks kept for safety.
        const custTotal = Number(sd.effectiveTotal) > 0 ? Number(sd.effectiveTotal)
            : (Number(sd.quotationTotal) > 0 ? Number(sd.quotationTotal) : ((Number(sd.finalPrice) || 0) + (Number(sd.addOnPrice) || 0)));
        const paidPct = (sd.paidPct != null) ? Number(sd.paidPct)
            : (custTotal ? Math.round((Number(sd.paidTotal) || 0) / custTotal * 100) : 0);
        const claimState = sd.claimState || 'NotReady';
        const commClaimed = (Number(cp.firstPayment) || 0) + (Number(cp.secondPayment) || 0)
            + (Number(cp.repayment) || 0) + (Number(cp.repayment2) || 0) + (Number(cp.repayment3) || 0)
            + (Number(cp.repayment4) || 0) + (Number(cp.repayment5) || 0);
        const commPct = cp.commPay ? Math.round(commClaimed / cp.commPay * 100) : 0;
        return { sd, cp, custTotal, paidPct, claimState, commClaimed, commPct };
    };
    // Commission entitlement FOLLOWS THE CUSTOMER'S PAYMENT MILESTONE: customer paid 60% → 60% of
    // the commission is claimable from Astern; customer paid 100% → all of it. (user 2026-08-18)
    const CLAIM_TARGET_PCT = { ReadyPartial: 60, ReadyFull: 100 };
    // Claimable RIGHT NOW = entitlement at the current milestone − what has already been claimed.
    // Compared in ringgit rather than the rounded percentages, so a sale genuinely at 59.6% is not
    // excluded by commPct rounding up to 60.
    const claimableNow = (m) => {
        const target = CLAIM_TARGET_PCT[m.claimState];
        if (!target) return 0;                                   // NotReady / disputed
        const entitled = (Number(m.cp.commPay) || 0) * target / 100;
        return Math.round((entitled - m.commClaimed) * 100) / 100;
    };

    // Claim-export row. Column set + order agreed with the user 2026-08-18 so the CSV can be
    // pasted straight into the Astern claim sheet. "Agent Discount" is the same figure that
    // sheet calls "Webbey Discount". Customer paid and customer total used to be glued into a
    // single "60 / 20100" string — they are two numeric columns now so the sheet can sum and
    // compare them. Credit Card was dropped (sits outside Total Collected). Sales ID is kept as
    // the row identifier; the sheet's own "No" is just a row counter and is not exported.
    const mapClaimRow = (r) => {
        const m = claimMetrics(r);
        const { sd, cp, custTotal, paidPct, commClaimed, commPct } = m;
        const commTotal = Number(cp.commPay) || 0;
        return {
            'Sales ID': sd.salesId ?? '',
            'Name': sd.customerName ?? '',
            // — price block —
            'Ori Price': sd.originalPrice ?? '',
            'Astern Discount': sd.discountByAstern ?? '',
            'Agent Discount': sd.discountByAgent ?? '',
            // NOTE: Upline/Team discount are deliberately NOT columns here. This CSV's
            // 14-column layout was agreed with the user 2026-08-18 and is pasted straight
            // into Astern's claim sheet — inserting columns shifts everything after them
            // and breaks that paste. They still net into Final Price. (2026-08-19)
            'Final Price': sd.finalPrice ?? '',
            'Add On': sd.addOnPrice ?? '',
            // — customer payment block —
            'Customer Paid Amount': Number(sd.paidTotal) || 0,
            'Customer Total Amount': custTotal,
            '% Customer Paid': paidPct,
            // — commission block: total, what has been claimed, what is still owed —
            'Commission Total': commTotal,
            'Already Claimed': commClaimed,
            // What Astern owes AT THIS MILESTONE, not the whole outstanding balance: a RM10,000
            // commission on a 60%-paid sale claims RM6,000 now, with the rest following at 100%.
            'Pending To Claim': claimableNow(m),
            '% Comm Progress': commPct,
        };
    };
    // Ready to Claim = customer reached a clean 60% or 100% milestone (ClaimState ReadyPartial/
    // ReadyFull). Disputed (off-milestone) + NotReady are excluded — this IS the "not disputed"
    // filter. Pending Comm = commission owed but not yet fully claimed.
    const handleExportReadyToClaim = () => {
        // Only sales with something actually left to claim. Excludes those already claimed to
        // 100%, and those whose commission progress has caught up with the customer's payment
        // progress (e.g. customer 60% paid and 60% of commission already claimed).
        // A NEGATIVE commission row (Upline/Team discount larger than the recipient's DOC/TOC
        // override) has a negative claimableNow, and a bare `> 0` filter would DROP it — the
        // claim sheet would then overstate what Astern owes, because the negative never nets
        // against the positive rows. Negative-entitlement rows ride along; over-claimed
        // POSITIVE rows stay excluded exactly as before. (spec §8.3)
        const rows = (fetchData || []).filter(r => {
            const m = claimMetrics(r);
            const c = claimableNow(m);
            return (Number(m.cp.commPay) || 0) < 0 ? c !== 0 : c > 0;
        }).map(mapClaimRow);
        const cfg = mkConfig({ filename: 'Ready_To_Claim_Astern', fieldSeparator: ',', useKeysAsHeaders: true });
        download(cfg)(generateCsv(cfg)(rows.length ? rows : [{}]));
    };
    const handleExportPendingClaim = () => {
        // `commPay > 0` alone silently dropped every NEGATIVE commission row, understating the
        // outstanding position. Mirror the test for negatives: still-outstanding means "claimed
        // has not yet reached the target", whichever side of zero the target sits on. (spec §8.3)
        const rows = (fetchData || []).filter(r => {
            const m = claimMetrics(r);
            const total = Number(m.cp.commPay) || 0;
            if (total > 0) return m.commClaimed < total;
            if (total < 0) return m.commClaimed > total;
            return false;
        }).map(mapClaimRow);
        const cfg = mkConfig({ filename: 'Pending_Comm_Claim', fieldSeparator: ',', useKeysAsHeaders: true });
        download(cfg)(generateCsv(cfg)(rows.length ? rows : [{}]));
    };

    const handleExportSelected = (rows) => {
        const csv = generateCsv(csvConfig)(mapCommForExport(rows.map(r => r.original)));
        download(csvConfig)(csv);
    };

    // Email the Ready-to-Claim sheet now (also auto-sent on the days set in General Settings).
    // The same xlsx the button beside this one downloads rides along as an attachment, and
    // Astern's contacts are Cc'd — so this sends the whole claim, not a heads-up about it.
    const handleSendClaimEmail = async () => {
        if (!window.confirm('Email the Ready-to-Claim sheet now?\n\nGoes to every active Accountant plus the addresses in General Settings, with Astern Cc’d and the xlsx attached.')) return;
        try {
            const { data } = await sendClaimEmailNow();
            window.alert(data?.note || (data?.sent ? 'Sent.' : 'Send failed.'));
        } catch (err) {
            window.alert(err?.response?.data?.message || err.message || 'Send failed.');
        }
    };

    const table = useMaterialReactTable({
        // Excel-style column filters: a search box over a tick-box list of the values
        // actually in that column, for every column at once. A column with nearly one
        // value per row falls back to the plain box on its own.
        // (user 2026-10-06: "for all table maybe yes. go ahead with this method")
        ...facetFilterOptions,
        columns,
        data: fetchData || [],
        enableColumnActions: false,
        enableColumnDragging: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableStickyHeader: true,
        enableRowSelection: true,
        // Keep the operator where they were after a bulk payout refetches the list.
        // TanStack resets the page index, sorting and filters whenever the data array
        // changes, so paying a batch on page 6 threw you back to page 1 and you had to
        // find your place again. (user 2026-08-21)
        autoResetPageIndex: false,
        autoResetExpanded: false,
        autoResetAll: false,
        // Mobile: let vertical scroll flow to the page (no inner table scroll box).
        // Wide tables still scroll horizontally; sticky header just won't trap scroll.
        muiTableContainerProps: { sx: { maxHeight: 'unset' } },
        initialState: { density: 'compact', showGlobalFilter: true },
        state: { globalFilter: ts.globalFilter, sorting: ts.sorting, pagination: ts.pagination, columnFilters: ts.columnFilters },
        onGlobalFilterChange: ts.setGlobalFilter,
        onSortingChange: ts.setSorting,
        onPaginationChange: ts.setPagination,
        onColumnFiltersChange: ts.setColumnFilters,
        muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
        muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
        muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
        muiTableBodyRowProps: ({ row, table }) => ({
            onClick: (e) => {
                if (e.target.closest('input[type="checkbox"]') || (e.target.tagName === 'SPAN' && e.target.style.cursor === 'pointer')) return;
                // While a selection is in progress, a row click TOGGLES the row instead of
                // navigating away. Picking a dozen rows for a bulk payout and losing the lot
                // to one stray click is the kind of thing you only forgive once.
                // (user 2026-08-21)
                if (table.getSelectedRowModel().rows.length > 0) {
                    row.toggleSelected();
                    return;
                }
                // An Astern-borne referral fee has no CommissionProgress row — it is kept
                // out of the commission pot deliberately — so it carries commProgressId 0.
                // Navigating would land on /accounting/commission/0 and show "Commission
                // record not found." Hiding the Edit link was not enough: the whole ROW is
                // the bigger click target, and it is what people actually hit.
                // (user 2026-10-14, All RF page)
                //
                // rowLink overrides all of that: on All RF the destination is a sale, not a
                // commission record, so every row has somewhere to go.
                const custom = rowLink ? rowLink(row.original) : null;
                const clickable = custom || ((onRowClick || detailRoute) && Number(row.original.commProgressId) > 0);
                if (!clickable) return;
                const url = custom
                    ? custom
                    : detailRoute
                        ? `${detailRoute}/${row.original.commProgressId}`
                        : `/accounting/commission/${row.original.commProgressId}`;
                if (e.ctrlKey || e.metaKey) {
                    window.open(url, '_blank');
                } else {
                    navigate(url);
                }
            },
            // The cursor has to agree with the click. A row that shows a pointer and a hover
            // highlight but goes nowhere reads as broken rather than as "not applicable".
            sx: (() => {
                const rowClickable = (rowLink && rowLink(row.original))
                    || ((onRowClick || detailRoute) && Number(row.original.commProgressId) > 0);
                return {
                    cursor: rowClickable ? 'pointer' : 'default',
                    '&:hover': { backgroundColor: rowClickable ? '#F0F7FF' : '#F8FAFC', transition: 'background-color 0.12s' },
                };
            })(),
        }),
        muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
        muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
        muiSearchTextFieldProps: { placeholder: 'Search...', sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#F8FAFC', fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif" } } },
        muiPaginationProps: { shape: 'rounded', showRowsPerPage: true, rowsPerPageOptions: [10, 25, 50, 100] },
        renderTopToolbarCustomActions: ({ table }) => {
            const selectedRows = table.getSelectedRowModel().rows;
            const hasSelection = selectedRows.length > 0;
            return (
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button onClick={handleExportAll} style={{ border: '1px solid #E2E8F0', background: 'white', borderRadius: '7px', padding: '6px 14px', fontSize: '0.775rem', fontWeight: 500, color: '#374151', cursor: 'pointer', fontFamily: "'Poppins', sans-serif", display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <DownloadIcon size={14} /> Export All
                    </button>
                    {showClaimExports && (
                        <button onClick={handleExportReadyToClaim} style={{ border: '1px solid #16A34A', background: '#F0FDF4', borderRadius: '7px', padding: '6px 14px', fontSize: '0.775rem', fontWeight: 600, color: '#15803D', cursor: 'pointer', fontFamily: "'Poppins', sans-serif", display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <DownloadIcon size={14} /> Ready to Claim (Astern)
                        </button>
                    )}
                    {showClaimExports && (
                        <button onClick={handleSendClaimEmail} style={{ border: '1px solid #16A34A', background: 'white', borderRadius: '7px', padding: '6px 14px', fontSize: '0.775rem', fontWeight: 600, color: '#15803D', cursor: 'pointer', fontFamily: "'Poppins', sans-serif", display: 'flex', alignItems: 'center', gap: '6px' }}>
                            Email claim sheet
                        </button>
                    )}
                    {hasSelection && (
                        <button onClick={() => handleExportSelected(selectedRows)} style={{ border: '1px solid #1A8CFF', background: '#EFF6FF', borderRadius: '7px', padding: '6px 14px', fontSize: '0.775rem', fontWeight: 500, color: '#1A8CFF', cursor: 'pointer', fontFamily: "'Poppins', sans-serif", display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <DownloadIcon size={14} /> Export Selected ({selectedRows.length})
                        </button>
                    )}
                    {/* Bulk payout (2026-08-20). Opt-in per page via showBulkPayout so it
                        only appears where an accountant actually keys payments. */}
                    {hasSelection && showBulkPayout && (
                        <button onClick={() => { setBulkRows(selectedRows); setBulkOpen(true); }} style={{ border: '1px solid #15803D', background: '#F0FDF4', borderRadius: '7px', padding: '6px 14px', fontSize: '0.775rem', fontWeight: 600, color: '#15803D', cursor: 'pointer', fontFamily: "'Poppins', sans-serif", display: 'flex', alignItems: 'center', gap: '6px' }}>
                            Pay Selected ({selectedRows.length})
                        </button>
                    )}
                </Box>
            );
        },
        renderBottomToolbarCustomActions: ({ table }) => {
            const filteredRows = table.getFilteredRowModel().rows;
            const caseCount = filteredRows.length;
            // Sums the FILTERED rows including NEGATIVE ones — a −RM151.47 DOC must reduce the
            // total, never be dropped or floored. Rounded to 2 dp so float residue can never
            // surface as "- RM 0.00".
            const totalCommission = Math.round(filteredRows.reduce((sum, r) => sum + (Number(r.original?.commissionProgressDto?.commPay) || 0), 0) * 100) / 100;
            return (
                <Typography component="div" sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#475569', fontFamily: "'Poppins', sans-serif", pl: '8px' }}>
                    <span>Cases: {caseCount}</span>
                    <span>{'·'}</span>
                    <span style={{ color: totalCommission < 0 ? '#DC2626' : undefined, fontWeight: totalCommission < 0 ? 700 : undefined }}>Total Commission: {handlePriceWithRM(totalCommission === 0 ? 0 : totalCommission)}</span>
                </Typography>
            );
        },
    });

    return (
        <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <MaterialReactTable table={table} />
            {showBulkPayout && (
                <BulkPayoutDialog
                    open={bulkOpen}
                    onClose={() => setBulkOpen(false)}
                    selectedRows={bulkRows}
                    isSuperAdmin={isSuperAdmin}
                    onDone={(res) => {
                        // Paid rows are done with, so drop the ticks. Only on a SUCCESSFUL
                        // write — cancelling leaves the selection exactly as it was, because
                        // rebuilding a dozen ticks after a mis-click is its own punishment.
                        // (user 2026-08-21)
                        table.resetRowSelection();
                        // Hold the scroll across the refetch. The list never unmounts here,
                        // so useTableState's mount-time restore does not apply — the rows
                        // simply swap underneath and the browser drops us to the top.
                        const y = window.scrollY;
                        onBulkDone && onBulkDone(res);
                        // Two frames: one for the new data to commit, one for the rows to lay out.
                        requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, y)));
                    }}
                />
            )}
        </div>
    );
}
